import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { fetchAllBadges, fetchBadgeCounts } from '../../api/badges';
import LoadingSpinner from '../../components/LoadingSpinner';

const COLUMNS = [
    { key: 'groupKey', label: 'Group' },
    { key: 'tier', label: 'Tier' },
    { key: 'name', label: 'Name' },
    { key: 'description', label: 'Description' },
    { key: 'checkerKey', label: 'Checker' },
    { key: 'flavorText', label: 'Flavor Text' },
    { key: 'users', label: 'Users' },
];

const AdminBadgesPage = () => {
    const [badges, setBadges] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [countsLoading, setCountsLoading] = useState(true);
    const [sortKey, setSortKey] = useState('groupKey');
    const [sortDir, setSortDir] = useState('asc');

    useEffect(() => {
        const loadBadges = async () => {
            try {
                const data = await fetchAllBadges();
                setBadges(data);
            } catch {
                toast.error('Failed to load badges');
            } finally {
                setLoading(false);
            }
        };

        const loadCounts = async () => {
            try {
                const data = await fetchBadgeCounts();
                setCounts(data);
            } catch {
                toast.error('Failed to load badge counts');
            } finally {
                setCountsLoading(false);
            }
        };

        loadBadges();
        loadCounts();
    }, []);

    const handleSort = useCallback((key) => {
        setSortDir(prev => sortKey === key ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
        setSortKey(key);
    }, [sortKey]);

    const sortedBadges = useMemo(() => {
        const sorted = [...badges].sort((a, b) => {
            let aVal = sortKey === 'users' ? (counts[a.badgeID] ?? 0) : a[sortKey];
            let bVal = sortKey === 'users' ? (counts[b.badgeID] ?? 0) : b[sortKey];
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();
            if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    }, [badges, counts, sortKey, sortDir]);

    if (loading) return <LoadingSpinner />;

    return (
        <div className="admin-page">
            <h2>Badges</h2>
            <p>{badges.length} badges total</p>

            <div className="table-scroll-wrapper">
                <table>
                    <thead>
                        <tr>
                            {COLUMNS.map(col => (
                                <th
                                    key={col.key}
                                    onClick={() => handleSort(col.key)}
                                    style={{ cursor: 'pointer', userSelect: 'none' }}
                                >
                                    {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sortedBadges.map(b => (
                            <tr key={b.badgeID}>
                                <td>{b.groupKey}</td>
                                <td>{b.tier}</td>
                                <td>{b.name}</td>
                                <td>{b.description}</td>
                                <td><code>{b.checkerKey}</code></td>
                                <td>{b.flavorText}</td>
                                <td>
                                    {countsLoading
                                        ? '...'
                                        : counts[b.badgeID] ?? 0}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminBadgesPage;
